import { type Dispatch, type ReactNode, type SetStateAction, useEffect, useRef, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { Dialog, DialogActions, DialogContent, DialogHeader } from "~/common/mui/Dialog/Dialog";
import {
  type ConfiguredQuickEntryItem,
  type QuickEntryForm,
  type QuickEntryItemPC,
  type QuickEntryPCForm,
} from "../types/models";
import { hasProductConfigured } from "../utils/helpers";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import CenterContents from "~/common/components/CenterContents";
import { LinkButton } from "~/common/components/Link";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { Button, Stack, Typography, styled } from "~/common/mui";
import { getEmptyRow } from "~/features/quickEntry/constants/constants";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "~/common/utils/screenSizes";
import { QuickEntryTableRow } from "~/features/quickEntry/v2/QuickEntryTableRow";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import { usePriceReferenceSelection } from "~/app/config/useSession";

interface Props {
  onClose: () => void;
  maxAmount?: number;
  maxAmountMessage?: ValueProvider<ReactNode>;
  validateRow?: (entry: ConfiguredQuickEntryItem, itemsPopup?: QuickEntryItemPC[]) => ReactNode[];

  onAdd: (form: QuickEntryPCForm, setIsSending: Dispatch<SetStateAction<boolean>>, onClose: () => void) => void;
  variant?: "pc" | "wishlist" | "b2c-cart";
  disableAdd?: boolean;
  initialEmptyRowAmount?: number;
  addButtonLabel: ReactNode;
}

const PC_RIGHT_COLS = ["61.2%", "38.8%"] as const;
const WISHLIST_RIGHT_COLS = ["44.9%", "43%", "12%"] as const;

const getRightSideColumns = (variant: Props["variant"]) => (variant === "pc" ? PC_RIGHT_COLS : WISHLIST_RIGHT_COLS);

export const QuickEntryPopup = ({
  onClose,
  maxAmount = 0,
  validateRow,
  maxAmountMessage,
  onAdd,
  disableAdd,
  initialEmptyRowAmount = 1,
  variant = "pc",
  addButtonLabel,
}: Props) => {
  const { t } = useTranslation();
  const { selection, status } = usePriceReferenceSelection();

  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  const defaultValues = useState<QuickEntryForm>(() => ({ items: [] }))[0];

  const formProps = useForm<QuickEntryForm>({
    mode: "onBlur",
    defaultValues: defaultValues,
    shouldUnregister: false,
  });
  const { fields, remove, append, replace } = useFieldArray({ control: formProps.control, name: "items" });
  const initialRowsAdded = useRef(false);

  useEffect(() => {
    if (status !== "ready" || initialRowsAdded.current) {
      return;
    }
    initialRowsAdded.current = true;
    replace(Array.from({ length: initialEmptyRowAmount }, () => getEmptyRow(selection)));
  }, [initialEmptyRowAmount, replace, selection, status]);

  const [isSending, setSending] = useState(false);

  const formRef = useRef<HTMLButtonElement>(null);
  const onSubmitClick = () => {
    return formRef.current?.click();
  };

  const watchItemsArray = formProps.watch("items");
  const items = fields.map((field, index) => {
    return {
      ...field,
      ...watchItemsArray[index],
    };
  });

  const itemsWithConfiguredProduct: ConfiguredQuickEntryItem[] = items.filter(
    hasProductConfigured,
  ) as ConfiguredQuickEntryItem[];

  const containErrors = !!formProps.formState.errors.items;

  const isAddToCartDisabled =
    itemsWithConfiguredProduct.length === 0 ||
    containErrors ||
    status !== "ready" ||
    disableAdd ||
    (validateRow && itemsWithConfiguredProduct.some((row) => validateRow(row, items).length > 0));

  const onRemoveAllItems = () => {
    formProps.setValue("items", [getEmptyRow(selection)]);
  };

  const maxReached = maxAmount && fields.length >= maxAmount;

  const appendNextRow = () => {
    if (!maxReached && status === "ready") {
      append(getEmptyRow(selection));
    }
  };

  const rightSideColumns = getRightSideColumns(variant);

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth={"desktop-l"}
      slotProps={{
        paper: {
          sx: {
            margin: { phone: 0, tablet: 4 },
            maxHeight: { phone: "100dvh", tablet: "90%" },
            height: { phone: "100dvh", tablet: "auto" },
            maxWidth: "1200px",
            width: { phone: "100%", tablet: "720px", "desktop-s": "856px", "desktop-m": "780px", "desktop-l": "880px" },
          },
        },
      }}
    >
      <DialogHeader onClose={onClose}>
        <Typography variant={"headlineSmall"}>{t("modal.quickEntry.title")}</Typography>
      </DialogHeader>

      <DialogContent>
        <FormProvider {...formProps}>
          <form
            onSubmit={formProps.handleSubmit((form) => onAdd(form, setSending, onClose))}
            className={isSending ? "v-hidden" : undefined}
          >
            <button ref={formRef} type={"submit"} hidden />
            <Stack gap={2}>
              {items.map((item, index) => (
                <QuickEntryTableRow
                  key={item.id}
                  item={item}
                  index={index}
                  onRemove={() => {
                    if (fields.length > 1) {
                      remove(index);
                    } else {
                      onRemoveAllItems();
                    }
                  }}
                  items={items}
                  validateRow={validateRow}
                  variant={variant}
                  rightSideColumns={rightSideColumns}
                />
              ))}

              {!maxReached ? (
                <LinkButton
                  style={{ width: "fit-content" }}
                  type={"button"}
                  onFocus={isDesktop ? appendNextRow : undefined}
                  onClick={!isDesktop ? appendNextRow : undefined}
                  data-test-id={"btn-quick-entry-add-new-product"}
                  iconBefore={<AddIcon />}
                >
                  {t("modal.quickEntry.newRow")}
                </LinkButton>
              ) : (
                render(maxAmountMessage)
              )}
            </Stack>
          </form>

          {isSending && (
            <SpinnerOverlay>
              <CircleSpinner />
            </SpinnerOverlay>
          )}
        </FormProvider>
      </DialogContent>

      <DialogActions>
        <Stack direction="row" gap={1}>
          <Button variant={"text"} onClick={onClose} data-test-id={"btn-quick-entry-cancel"}>
            {t("cancel")}
          </Button>
          <Button onClick={onSubmitClick} disabled={isAddToCartDisabled} data-test-id={"btn-quick-entry-submit"}>
            {addButtonLabel}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

type ValueProvider<T> = T | (() => T);
function render(val: ValueProvider<ReactNode>) {
  return typeof val === "function" ? val() : val;
}

const SpinnerOverlay = styled(CenterContents)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
});
